package com.xtream.ui.tv.presentation.common

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.material3.BottomSheetScaffold
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SheetValue
import androidx.compose.material3.rememberBottomSheetScaffoldState
import androidx.compose.material3.rememberStandardBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.focusRestorer
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.LifecycleResumeEffect
import androidx.paging.compose.LazyPagingItems
import androidx.paging.compose.itemKey
import com.xtream.data_kmp.dto.BaseStream

@OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)
@Composable
fun ContentGridTvScreen(
    modifier: Modifier = Modifier,
    title: @Composable (() -> Unit)? = null,
    subtitle: @Composable (() -> Unit)? = null,
    actions: @Composable (RowScope.() -> Unit)? = null, // Preserved signature for callers
    carousel: (@Composable () -> Unit)? = null,
    lazyPagingItems: LazyPagingItems<out BaseStream>,
    numColumns: Int = 3,
    scrollState: LazyGridState = rememberLazyGridState(),
    rowIItem: @Composable ((BaseStream?) -> Unit) = {},
) {
    val scope = rememberCoroutineScope()

    // 1. Material 3 Bottom Sheet State tracking (Collapsed maps to PartiallyExpanded)
    val bottomSheetState = rememberStandardBottomSheetState(
        initialValue = SheetValue.PartiallyExpanded,
        skipHiddenState = true
    )
    val scaffoldState = rememberBottomSheetScaffoldState(bottomSheetState)

    val density = LocalDensity.current
    val windowHeightPx = LocalWindowInfo.current.containerSize.height

    val backLayerHeight = 85.dp
    val bottomSheetPeekHeight = with(density) { windowHeightPx.toDp() - backLayerHeight }
    val focusRequester = remember { FocusRequester() }

    BottomSheetScaffold(
        modifier = modifier,
        scaffoldState = scaffoldState,
        sheetPeekHeight = bottomSheetPeekHeight,
        sheetShape = RectangleShape,
        sheetMaxWidth = Dp.Unspecified,

        // 2. Material 3 Color Mappings
        containerColor = Color.Transparent,
        sheetContainerColor = MaterialTheme.colorScheme.background,

        // 3. M3 Gesture configuration updates
        sheetSwipeEnabled = false,
        sheetShadowElevation = 0.dp,
        sheetTonalElevation = 0.dp,

        // 4. Hides the default touch-drag handle pill since this is an Android TV interface
        sheetDragHandle = null,

        content = { paddingValues ->
            Column(
                modifier = Modifier
                    .padding(paddingValues) // Respect M3 scaffold content padding
                    .padding(top = 24.dp)
                    .padding(horizontal = 48.dp)
            ) {
                title?.invoke()
                subtitle?.invoke()
            }
        },
        sheetContent = {
            LazyVerticalGrid(
                columns = GridCells.Fixed(numColumns),
                state = scrollState,
                contentPadding = PaddingValues(horizontal = 48.dp, vertical = 24.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp),
                modifier = Modifier
                    .focusRequester(focusRequester)
                    .focusRestorer()
                    .fillMaxSize()

            ) {
                // Preserved your carousel sticky header implementation
                if (carousel != null) {
                    stickyHeader {
                        carousel()
                    }
                }

                items(
                    count = lazyPagingItems.itemCount,
                    key = lazyPagingItems.itemKey { it.streamId },
                    contentType = { null }
                ) { index ->
                    val stream = lazyPagingItems[index]
                    rowIItem.invoke(stream)
                }
            }
        }
    )

    // 5. Reactive expansion updates using M3 SheetValue states
    val isAtTop by remember {
        derivedStateOf {
            scrollState.firstVisibleItemIndex == 0 &&
                    scrollState.firstVisibleItemScrollOffset == 0
        }
    }

    LaunchedEffect(isAtTop) {
        if (!isAtTop) {
            bottomSheetState.expand()
        } else {
            // collapse() is replaced by partialExpand() in M3 standard sheets
            bottomSheetState.partialExpand()
        }
    }

    // 6. Focus retention hooks preserved for your Jetpack Compose TV platform engine
    LifecycleResumeEffect(Unit) {
        onPauseOrDispose {
            focusRequester.saveFocusedChild()
        }
    }
}